import { Component, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { DataService } from 'src/app/services/data/data.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { CpPo } from 'src/app/services/types/cp-po.model';

/**
 * The five things this modal can be showing. Kept as an explicit union rather
 * than a pile of booleans so the three required states can never overlap.
 */
export type PoViewerState =
  | 'loading'
  | 'ready'       // shared, has line items
  | 'empty'       // shared, but nothing has been costed yet
  | 'not-shared'  // the server-side manager gate is on and unchecked
  | 'error';      // request failed / no assignment for this partner + job

/**
 * LH-51 (LH-38 Phase 2) — partner-facing Contract Partner Purchase Order viewer.
 *
 * Presentation ONLY. Every rule that decides what a partner is allowed to see,
 * which table the line items come from and what each rate/amount is has already
 * been applied by the Flask backend (GET /qb/job-switcher/cp-po/<guid>?ospId=).
 * There are deliberately no QuickBase field ids, no rate resolution and no
 * arithmetic in this component beyond Intl display formatting — the webview has
 * no QuickBase token and must never become a second implementation of the
 * "Generate CP PO" code page's rules.
 *
 * The request is issued here rather than on DailyImporterV3Page because that page
 * is ChangeDetectionStrategy.OnPush; this component uses default change detection
 * (like both existing modals), so the async response renders with no markForCheck
 * plumbing and the page gains no new async state.
 */
@Component({
  selector: 'app-po-viewer',
  templateUrl: 'po-viewer.component.html',
  styleUrls: ['./po-viewer.component.scss'],
  providers: [DataService],
})
export class PoViewerComponent implements OnInit {
  /** Passed in via componentProps by DailyImporterV3Page. */
  guid?: string;
  ospId?: string | number;

  state: PoViewerState = 'loading';
  po?: CpPo;
  errorMessage?: string;
  /** False when the failure is permanent (404) and Retry would only frustrate. */
  canRetry = true;
  /**
   * Brand logos are bundled webview assets, but an unrecognised brand resolves to
   * no asset at all. Hide the <img> rather than show a broken-image icon on a
   * financial document.
   */
  logoFailed = false;

  constructor(
    private modalCtrl: ModalController,
    private dataService: DataService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.loadPo();
  }

  cancel() {
    return this.modalCtrl.dismiss(null, 'cancel');
  }

  retry() {
    this.loadPo();
  }

  loadPo() {
    this.state = 'loading';
    this.po = undefined;
    this.errorMessage = undefined;
    this.canRetry = true;
    this.logoFailed = false;

    // Both are required: the backend scopes the purchase order with
    // guid AND ospId, and the assignment is never accepted from the client.
    if (!this.guid || !this.ospId) {
      this.showError('Your job could not be identified. Close this window, select your job again and try once more.');
      return;
    }

    this.dataService.getCpPo(this.guid, this.ospId).subscribe(
      (data) => {
        const po = new CpPo(data);

        if (po.shared === false) {
          this.state = 'not-shared';
          return;
        }

        // A success payload with no header is not a $0 purchase order — treat it
        // as a failure so an empty document can never be mistaken for a real one.
        if (!po.header) {
          this.showError('The Purchase Order came back incomplete. Please try again.');
          return;
        }

        this.po = po;
        this.state = po.lineItems.length > 0 ? 'ready' : 'empty';
      },
      (error) => {
        console.log(error);

        // HttpClient routes EVERY non-2xx here, so the backend's distinct states
        // have to be recovered from the status. A 404 means "no purchase order
        // for this partner on this job" — a normal, permanent answer. Showing it
        // as a connection problem would hand the partner a Retry that can never
        // succeed, and blame their network for our data.
        if (error?.status === 404) {
          this.showError('No Purchase Order has been set up for this job yet.', false);
          return;
        }

        this.showError('We could not reach the server. Check your connection and try again.');

        this.toastService.presentToast(
          'Error',
          'Error while loading your Purchase Order',
          'top',
          'danger',
          4000
        );
      }
    );
  }

  // -------------------------------------------------------------------------
  //  Print / Save as PDF
  //
  //  An `@media print` stylesheet cannot work from inside an ion-modal: both
  //  ion-modal and ion-content use shadow DOM, ion-content's .inner-scroll is
  //  absolutely positioned with `contain: size style`, and @ionic/angular's
  //  structure.css pins `body { position: fixed; height: 100% }` — together
  //  those clip the printout to a single screenful and are unreachable from a
  //  global print rule. So the PO is written into a new window as a plain,
  //  self-contained document, which is also exactly what the manager gets from
  //  the QuickBase code page + browser Save-as-PDF.
  // -------------------------------------------------------------------------
  printPo() {
    if (this.state !== 'ready' || !this.po) {
      return;
    }

    // Must be synchronous with the click, or the pop-up blocker eats it.
    const printWindow = window.open('', '_blank');

    if (!printWindow) {
      this.toastService.presentToast(
        'Pop-up blocked',
        'Allow pop-ups for this site to print your Purchase Order',
        'top',
        'danger',
        4000
      );
      return;
    }

    printWindow.document.open();
    printWindow.document.write(this.buildPrintableHtml(this.po));
    printWindow.document.close();

    // onload waits for the logo image, so it never prints a half-drawn header.
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  }

  /** `canRetry` is false for permanent answers (e.g. 404) where retrying cannot help. */
  private showError(message: string, canRetry = true) {
    this.state = 'error';
    this.errorMessage = message;
    this.canRetry = canRetry;
  }

  /** 5-entity escape, mirroring escapeHtml() in ContractPartnerPO.js. */
  private escapeHtml(value: any): string {
    return String(value === null || value === undefined ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Builds a standalone print document laid out like the manager-facing
   * "Generate CP PO" code page: logo top-left, Purchase Order top-right, the
   * from-block on the left with PO #/Date/Job ID/Job Name/Project on the right,
   * To: block, then the line items and a right-aligned TOTAL under Amount.
   */
  private buildPrintableHtml(po: CpPo): string {
    const esc = (v: any) => this.escapeHtml(v);
    const header = po.header;
    const from = header?.from;

    // The print document lives in an about:blank window, so a relative asset
    // path ('assets/logos/...') has no reliable base to resolve against —
    // absolutise it against the app's own base URL.
    const logo =
      from?.logoUrl && !this.logoFailed
        ? `<img class="logo" src="${esc(new URL(from.logoUrl, document.baseURI).href)}" alt="">`
        : '';

    const rows = po.lineItems
      .map(
        (li) => `<tr>
          <td class="num">${esc(li.planQtyFormatted)}</td>
          <td>${esc(li.billingCode)}</td>
          <td>${esc(li.description)}</td>
          <td class="num">${esc(li.rateFormatted)}</td>
          <td class="num">${esc(li.amountFormatted)}</td>
        </tr>`
      )
      .join('');

    return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>CP PO ${esc(header?.cpName)} - Job ${esc(header?.jobId)}</title>
<style>
  @page { margin: 12mm; }
  body { font-family: sans-serif; font-size: 12px; color: #000; margin: 0; padding: 16px; }
  .top { display: flex; align-items: flex-start; justify-content: space-between; }
  .logo { height: 90px; max-width: 320px; object-fit: contain; }
  .doc-title { font-size: 24px; font-weight: bold; white-space: nowrap; }
  hr.rule { border: none; border-top: 4px solid #000; margin: 8px 0 14px; }
  .meta { display: flex; justify-content: space-between; gap: 24px; }
  .from div { line-height: 1.5; }
  .from .name { font-weight: bold; }
  .fields td { padding: 1px 0; vertical-align: top; }
  .fields td.label { font-weight: bold; padding-right: 10px; white-space: nowrap; }
  .to { margin: 16px 0 12px; }
  .to .label { font-weight: bold; }
  .to .name { font-weight: bold; }
  table.items { width: 100%; border-collapse: collapse; margin-top: 8px; }
  table.items th, table.items td { border: 1px solid #000; padding: 4px 6px; }
  table.items th { background-color: #f1edf1; text-align: left; }
  table.items td { background-color: #fff; }
  table.items .num { text-align: right; white-space: nowrap; }
  table.items tfoot td { border: none; }
  table.items tfoot .total-label { text-align: right; font-weight: bold; padding-right: 16px; }
  table.items tfoot .total-value { text-align: right; font-weight: bold; white-space: nowrap; }
  .print-bar { margin-top: 24px; }
  .print-bar button { font-size: 14px; padding: 8px 16px; cursor: pointer; }
  @media print { .print-bar { display: none; } }
</style>
</head>
<body>
  <div class="top">
    <div>${logo}</div>
    <div class="doc-title">Purchase Order</div>
  </div>

  <hr class="rule">

  <div class="meta">
    <div class="from">
      <div class="name">${esc(from?.name)}</div>
      <div>${esc(from?.address)}</div>
      <div>${esc(from?.cityState)}</div>
      <div>${esc(from?.phone)}</div>
    </div>
    <table class="fields">
      <tr><td class="label">PO #:</td><td>${esc(header?.poNumber)}</td></tr>
      <tr><td class="label">Date:</td><td>${esc(header?.date)}</td></tr>
      <tr><td class="label">Job ID:</td><td>${esc(header?.jobId)}</td></tr>
      <tr><td class="label">Job Name:</td><td>${esc(header?.jobName)}</td></tr>
      <tr><td class="label">Project:</td><td>${esc(header?.projectNumber)}</td></tr>
    </table>
  </div>

  <div class="to">
    <span class="label">To:</span> <span class="name">${esc(header?.cpName)}</span>
    <div>${esc(header?.cpEmail)}</div>
  </div>

  <table class="items">
    <thead>
      <tr>
        <th class="num">Plan Qty</th>
        <th>Billing Code</th>
        <th>Description</th>
        <th class="num">Rate</th>
        <th class="num">Amount</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
    <tfoot>
      <tr>
        <td colspan="4" class="total-label">TOTAL:</td>
        <td class="total-value">${esc(po.totalFormatted)}</td>
      </tr>
    </tfoot>
  </table>

  <div class="print-bar">
    <button type="button" onclick="window.print()">Print / Save as PDF</button>
  </div>
</body>
</html>`;
  }
}
